import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { categoryName, formatCount, formatDate } from "@/utils/articles";

interface ArticleCardProps {
  article: Article;
  layout?: "grid" | "list";
}

export default function ArticleCard({ article, layout = "grid" }: ArticleCardProps) {
  if (layout === "list") {
    return (
      <article className="group flex flex-col sm:flex-row gap-4 md:gap-6 py-6 border-b border-background-200">
        <Link
          to={`/detail/${article.id}`}
          className="relative w-full sm:w-56 lg:w-64 aspect-[8/5] rounded-lg overflow-hidden bg-background-200 shrink-0"
        >
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 理论电影`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-center gap-2 mb-2">
            <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium">
              {categoryName(article.category)}
            </span>
            <span className="text-xs text-foreground-400">{formatDate(article.date)}</span>
          </div>
          <h3 className="font-heading text-lg md:text-xl font-semibold leading-snug text-foreground-950 mb-2">
            <Link to={`/detail/${article.id}`} className="hover:text-secondary-700 transition-colors">
              {article.title}
            </Link>
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
            {article.summary}
          </p>
          <div className="mt-auto flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line" />
              {formatCount(article.views)}
            </span>
            <span className="hidden sm:flex items-center gap-1.5">
              <i className="ri-bookmark-line" />
              {formatCount(article.favorites)}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-secondary-300 transition-colors">
      <Link
        to={`/detail/${article.id}`}
        className="relative w-full aspect-[8/5] overflow-hidden bg-background-200"
      >
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 理论电影`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="flex-1 flex flex-col p-4 md:p-5">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-900 text-xs font-medium">
            {categoryName(article.category)}
          </span>
          <span className="text-xs text-foreground-400">{formatDate(article.date)}</span>
        </div>
        <h3 className="font-heading text-base md:text-lg font-semibold leading-snug text-foreground-950 mb-2 line-clamp-2">
          <Link to={`/detail/${article.id}`} className="hover:text-secondary-700 transition-colors">
            {article.title}
          </Link>
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-4">
          {article.summary}
        </p>
        <div className="mt-auto pt-3 border-t border-background-100 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(article.views)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-bookmark-line" />
              {formatCount(article.favorites)}
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}